import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Download, Loader2, Lock, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { BlockView } from "@/components/DocPreview";
import { api, apiError, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function PublicProposal() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [doc, setDoc] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tier, setTier] = useState("");
  const [showAccept, setShowAccept] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [signer, setSigner] = useState({ name: "", role: "", email: "", agreed: false });
  const [declineForm, setDeclineForm] = useState({ name: "", reason: "" });
  const [decision, setDecision] = useState(null);
  const seen = useRef(new Set());
  const isPreview = new URLSearchParams(window.location.search).get("preview") === "1";
  const headers = isPreview ? { "X-Propojo-Preview": "1" } : {};

  const open = useCallback(
    async (pwd) => {
      setBusy(true);
      setError("");
      try {
        const { data } = await api.post(`/public/proposals/${token}`, { password: pwd || "" }, { headers });
        setDoc(data);
        setDecision(data.decision);
      } catch (e) {
        setError(apiError(e));
      } finally {
        setBusy(false);
      }
    },
    [token]
  );

  useEffect(() => {
    api
      .get(`/public/proposals/${token}/meta`)
      .then((r) => {
        setMeta(r.data);
        if (!r.data.requires_password) open("");
      })
      .catch((e) => setError(apiError(e)));
  }, [token, open]);

  useEffect(() => {
    if (!doc) return;
    const t = setInterval(() => {
      api
        .post(`/public/proposals/${token}/track`, { seconds: 15, sections: Array.from(seen.current) }, { headers })
        .catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [doc, token]);

  const download = async (kind) => {
    const id = toast.loading(`Preparing your ${kind.toUpperCase()}…`);
    try {
      const query = password ? `?password=${encodeURIComponent(password)}` : "";
      await downloadFile(`/public/proposals/${token}/export.${kind}${query}`, `proposal.${kind}`);
      toast.success(`${kind.toUpperCase()} downloaded`, { id });
    } catch (e) {
      toast.error(apiError(e), { id });
    }
  };

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/proposals/${token}/accept`, { ...signer, tier_id: tier });
      setDecision(data.decision);
      setShowAccept(false);
      toast.success("Proposal accepted");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/proposals/${token}/decline`, declineForm);
      setDecision(data.decision);
      setShowDecline(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (error && !doc && !meta?.requires_password)
    return (
      <div className="min-h-screen bg-app grid place-items-center px-4 text-center">
        <div>
          <p data-testid="public-error" className="text-lg text-zinc-200">{error}</p>
          <p className="text-sm text-zinc-500 mt-2">Please contact the sender for a new link.</p>
        </div>
      </div>
    );

  if (meta?.requires_password && !doc)
    return (
      <div className="min-h-screen nv-grid-bg grid place-items-center px-4">
        <div className="w-full max-w-sm nv-rise">
          <Lock className="w-5 h-5 text-indigo-400 mb-4" />
          <h1 className="text-2xl font-bold tracking-tight">{meta.title}</h1>
          <p className="text-sm text-zinc-500 mt-1 mb-5">This proposal is password protected.</p>
          <Input data-testid="public-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="h-11 bg-zinc-900/60 border-zinc-800" />
          {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
          <Button data-testid="public-unlock-btn" onClick={() => open(password)} disabled={busy} className="mt-3 w-full h-11 bg-indigo-500 hover:bg-indigo-600 text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "View proposal"}
          </Button>
        </div>
      </div>
    );

  if (!doc)
    return (
      <div className="min-h-screen bg-app grid place-items-center text-zinc-600">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  const b = doc.brand_kit || {};
  const brand = {
    accent_color: b.accent_color || "#6366F1",
    text_color: b.text_color || "#18181B",
    background_color: b.background_color || "#FFFFFF",
    heading_font: b.heading_font || "Plus Jakarta Sans",
    body_font: b.body_font || "Inter",
    cover_style: b.cover_style || "editorial",
    logo_url: b.logo_url || "",
    footer_text: b.footer_text || "",
  };
  const decided = decision || ["accepted", "declined"].includes(doc.status);

  return (
    <div className="min-h-screen" style={{ background: brand.background_color, color: brand.text_color }}>
      <div
        data-testid="client-viewer"
        className="nv-doc mx-auto max-w-3xl px-5 sm:px-8 pt-8 pb-52 sm:pb-36"
        style={{ "--doc-heading": brand.heading_font, "--doc-body": brand.body_font }}
      >
        {isPreview && (
          <p className="mb-4 text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded inline-block" style={{ background: `${brand.accent_color}18`, color: brand.accent_color }}>
            Owner preview — not tracked
          </p>
        )}
        {(doc.blocks || []).map((block) => (
          <div
            key={block.id}
            ref={(el) => {
              if (!el) return;
              const io = new IntersectionObserver((entries) => {
                entries.forEach((en) => en.isIntersecting && seen.current.add(block.id));
              });
              io.observe(el);
            }}
          >
            <BlockView
              block={block}
              brand={brand}
              currency={doc.currency}
              gstMode={doc.gst_mode}
              docTitle={doc.title}
              clientName={doc.client?.name}
              orgName={doc.org_name}
              onTierSelect={decided ? undefined : setTier}
              selectedTier={tier}
              decision={decision || doc.decision}
            />
          </div>
        ))}

        {doc.client?.gstin && <p className="text-xs opacity-50 mt-6 font-num">Client GSTIN: {doc.client.gstin}</p>}
        {brand.footer_text && <p className="text-xs opacity-40 mt-8">{brand.footer_text}</p>}
      </div>

      <div data-testid="sticky-cta-bar" className="fixed bottom-0 left-0 right-0 z-50 border-t backdrop-blur-lg" style={{ background: "rgba(255,255,255,0.94)", borderColor: "rgba(0,0,0,0.1)" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3">
          <div className="flex items-end justify-between sm:block sm:min-w-0">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.16em] opacity-50">Total investment</p>
              <p data-testid="viewer-total-amount" className="text-xl sm:text-lg font-bold font-num leading-tight">{doc.totals?.grand_total_formatted}</p>
            </div>
            <div className="flex gap-1.5 sm:hidden">
              <button
                data-testid="download-pdf-btn-mobile"
                onClick={() => download("pdf")}
                className="px-2.5 py-1.5 rounded-lg border border-black/15 text-[11px] flex items-center gap-1 opacity-70"
              >
                <Download className="w-3.5 h-3.5" /> PDF
              </button>
              <button
                data-testid="download-docx-btn-mobile"
                onClick={() => download("docx")}
                className="px-2.5 py-1.5 rounded-lg border border-black/15 text-[11px] flex items-center gap-1 opacity-70"
              >
                <Download className="w-3.5 h-3.5" /> Word
              </button>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-1.5 sm:ml-auto">
            <Button data-testid="download-pdf-btn" variant="outline" onClick={() => download("pdf")} className="h-11 border-black/15 text-black/70 hover:bg-black/5">
              <Download className="w-4 h-4 mr-1.5" /> PDF
            </Button>
            <Button data-testid="download-docx-btn" variant="outline" onClick={() => download("docx")} className="h-11 border-black/15 text-black/70 hover:bg-black/5">
              <Download className="w-4 h-4 mr-1.5" /> Word
            </Button>
          </div>

          {decided ? (
            <div data-testid="decision-recorded" className="sm:text-right">
              <p className="text-sm font-semibold" style={{ color: (decision?.outcome || doc.status) === "accepted" ? "#059669" : "#DC2626" }}>
                {(decision?.outcome || doc.status) === "accepted" ? "Accepted" : "Declined"}
              </p>
              {decision?.fingerprint && (
                <p className="text-[10px] font-num opacity-45 flex items-center gap-1 sm:justify-end">
                  <ShieldCheck className="w-3 h-3" /> {decision.fingerprint.slice(0, 16)}…
                </p>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                data-testid="decline-proposal-btn"
                variant="outline"
                onClick={() => setShowDecline(true)}
                className="h-11 flex-1 sm:flex-none border-black/15 text-black/60 hover:bg-black/5"
              >
                <X className="w-4 h-4 mr-1.5" /> Decline
              </Button>
              <Button
                data-testid="sticky-accept-proposal-btn"
                onClick={() => setShowAccept(true)}
                className="h-11 flex-[2] sm:flex-none text-white shadow-lg"
                style={{ background: brand.accent_color }}
              >
                <Check className="w-4 h-4 mr-1.5" /> Accept proposal
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showAccept} onOpenChange={setShowAccept}>
        <DialogContent className="bg-white text-[#18181b] max-w-sm">
          <DialogHeader>
            <DialogTitle>Accept this proposal</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="accept-name-input" placeholder="Your full name" value={signer.name} onChange={(e) => setSigner({ ...signer, name: e.target.value })} />
            <Input data-testid="accept-role-input" placeholder="Your role" value={signer.role} onChange={(e) => setSigner({ ...signer, role: e.target.value })} />
            <Input data-testid="accept-email-input" placeholder="Email" value={signer.email} onChange={(e) => setSigner({ ...signer, email: e.target.value })} />
            <label className="flex items-start gap-2 text-xs text-zinc-600">
              <input data-testid="accept-agree-checkbox" type="checkbox" className="mt-0.5" checked={signer.agreed} onChange={(e) => setSigner({ ...signer, agreed: e.target.checked })} />
              I have authority to accept this proposal and agree to the scope, pricing and terms set out above.
            </label>
            <Button data-testid="confirm-accept-btn" onClick={accept} disabled={busy || !signer.name || !signer.agreed} className="w-full h-11 text-white" style={{ background: brand.accent_color }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign & accept"}
            </Button>
            <p className="text-[10px] text-zinc-500">
              We record a tamper-evident cryptographic fingerprint of this document at the moment of acceptance.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDecline} onOpenChange={setShowDecline}>
        <DialogContent className="bg-white text-[#18181b] max-w-sm">
          <DialogHeader>
            <DialogTitle>Decline this proposal</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="decline-name-input" placeholder="Your name" value={declineForm.name} onChange={(e) => setDeclineForm({ ...declineForm, name: e.target.value })} />
            <Textarea data-testid="decline-reason-input" placeholder="Reason (optional, helps them improve)" value={declineForm.reason} onChange={(e) => setDeclineForm({ ...declineForm, reason: e.target.value })} />
            <Button data-testid="confirm-decline-btn" onClick={decline} disabled={busy || !declineForm.name} variant="outline" className="w-full h-11 border-zinc-300">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit decline"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
